/**
 * AnnotatedPDFViewer Component
 * ============================
 * A React component for displaying PDFs with medication annotations.
 * Uses iframe for reliable PDF display with download option.
 * 
 * No external PDF library dependencies required!
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { 
  Card, 
  Button, 
  Space, 
  Tooltip, 
  Spin, 
  Typography, 
  Badge,
  Divider,
  Tag,
  message,
  Alert,
  Table
} from 'antd';
import {
  DownloadOutlined,
  FileTextOutlined,
  MedicineBoxOutlined,
  ExclamationCircleOutlined,
  UnorderedListOutlined,
  EyeOutlined,
  CloseOutlined
} from '@ant-design/icons';

const { Text, Title } = Typography;

/**
 * Medication Legend Component
 */
const MedicationLegend = ({ medicationCount, totalReferences }) => {
  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '16px',
      padding: '8px 16px',
      backgroundColor: '#fafafa',
      borderRadius: '6px',
      flexWrap: 'wrap'
    }}>
      <Space>
        <MedicineBoxOutlined style={{ color: '#6200EE' }} />
        <Text strong>{medicationCount} Unique Medications</Text>
        <Text type="secondary">({totalReferences} references)</Text>
      </Space>
      <Divider type="vertical" />
      <Space size="large">
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ 
            width: '20px', 
            height: '12px', 
            backgroundColor: 'rgba(255, 235, 59, 0.6)',
            border: '1px solid #FFC107',
            borderRadius: '2px'
          }} />
          <Text>Prescription</Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ 
            width: '20px', 
            height: '12px', 
            backgroundColor: 'rgba(76, 175, 80, 0.5)',
            border: '1px solid #4CAF50',
            borderRadius: '2px'
          }} />
          <Text>OTC</Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ 
            width: '20px', 
            height: '12px', 
            backgroundColor: 'rgba(244, 67, 54, 0.5)',
            border: '1px solid #F44336',
            borderRadius: '2px'
          }} />
          <Text>Allergy</Text>
        </div>
      </Space>
    </div>
  );
};

/**
 * Medication List Component - Shows all found medications in a table
 */
const MedicationList = ({ annotations }) => {
  // Group annotations by medication name
  const medicationMap = new Map();
  
  annotations.forEach(ann => {
    const key = ann.medication_name?.toLowerCase() || ann.text?.toLowerCase();
    if (!medicationMap.has(key)) {
      medicationMap.set(key, {
        name: ann.medication_name || ann.text,
        category: ann.category,
        dosage: ann.dosage,
        frequency: ann.frequency,
        pages: new Set([ann.page]),
        count: 1
      });
    } else {
      const existing = medicationMap.get(key);
      existing.pages.add(ann.page);
      existing.count++;
    }
  });

  const medications = Array.from(medicationMap.values()).map((med, idx) => ({
    key: idx,
    name: med.name,
    category: med.category,
    dosage: med.dosage || '-',
    frequency: med.frequency || '-',
    pages: Array.from(med.pages).sort((a, b) => a - b).join(', '),
    references: med.count
  }));

  const columns = [
    {
      title: 'Medication',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Tag color={
            record.category === 'allergy' ? 'red' : 
            record.category === 'otc' ? 'green' : 
            'gold'
          }>
            {record.category?.toUpperCase() || 'RX'}
          </Tag>
          <Text strong>{text}</Text>
        </Space>
      )
    },
    {
      title: 'Dosage',
      dataIndex: 'dosage',
      key: 'dosage',
      width: 120
    },
    {
      title: 'Frequency',
      dataIndex: 'frequency',
      key: 'frequency',
      width: 120
    },
    {
      title: 'Page(s)',
      dataIndex: 'pages',
      key: 'pages',
      width: 80
    },
    {
      title: 'Refs',
      dataIndex: 'references',
      key: 'references',
      width: 60,
      align: 'center'
    }
  ];

  return (
    <Table 
      dataSource={medications}
      columns={columns}
      size="small"
      pagination={false}
      scroll={{ y: 300 }}
      style={{ marginTop: '12px' }}
    />
  );
};

/**
 * Main AnnotatedPDFViewer Component
 */
const AnnotatedPDFViewer = ({ 
  data, 
  title = "Annotated Document",
  sessionId,
  uuid,
  registerComponent
}) => {
  // Extract data from props
  const {
    original_filename = 'document.pdf',
    annotated_pdf_url,
    download_url,
    annotations = [],
    page_dimensions = [],
    medication_count = 0,
    total_annotations = 0
  } = data || {};

  // State
  const [viewMode, setViewMode] = useState('pdf'); // 'pdf' or 'list'
  const [loading, setLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);
  
  // Refs
  const containerRef = useRef(null);

  // Register component for updates
  useEffect(() => {
    if (registerComponent && uuid) {
      const unregister = registerComponent(() => {
        console.log('AnnotatedPDFViewer refresh triggered');
      });
      return unregister;
    }
  }, [registerComponent, uuid]);

  // Get base URL for the backend
  const getBaseUrl = () => {
    // Try to detect the backend URL
    // Adjust this based on your configuration
    return 'http://localhost:8028';
  };

  // Full URL for the PDF
  const pdfUrl = annotated_pdf_url 
    ? `${getBaseUrl()}${annotated_pdf_url}`
    : null;

  // Download handler
  const handleDownload = async () => {
    try {
      const downloadEndpoint = download_url || `/download_annotated/${annotated_pdf_url?.split('/').pop()}`;
      const fullUrl = `${getBaseUrl()}${downloadEndpoint}`;
      
      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${sessionId}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `annotated_${original_filename}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success('Download started');
    } catch (err) {
      console.error('Download error:', err);
      message.error('Failed to download PDF');
    }
  };

  // Open PDF in new tab
  const handleOpenInNewTab = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  // Count annotations per category
  const categoryCounts = annotations.reduce((acc, ann) => {
    const cat = ann.category || 'prescription';
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {});

  // Handle iframe load
  const handleIframeLoad = () => {
    setLoading(false);
  };

  // Handle iframe error
  const handleIframeError = () => {
    setLoading(false);
    setIframeError(true);
  };

  // If no data, show placeholder
  if (!data || !annotated_pdf_url) {
    return (
      <Card 
        title={
          <Space>
            <FileTextOutlined style={{ color: '#6200EE' }} />
            <span>{title}</span>
          </Space>
        }
      >
        <Alert
          message="No Document Available"
          description="Upload a medical record to see annotated results."
          type="info"
          showIcon
        />
      </Card>
    );
  }

  return (
    <Card 
      ref={containerRef}
      title={
        <Space>
          <FileTextOutlined style={{ color: '#6200EE' }} />
          <span>{title}</span>
          <Badge 
            count={medication_count} 
            style={{ backgroundColor: '#6200EE' }}
          />
        </Space>
      }
      extra={
        <Space>
          <Button 
            icon={viewMode === 'pdf' ? <UnorderedListOutlined /> : <EyeOutlined />}
            onClick={() => setViewMode(viewMode === 'pdf' ? 'list' : 'pdf')}
          >
            {viewMode === 'pdf' ? 'View List' : 'View PDF'}
          </Button>
          <Button 
            type="primary" 
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            style={{ backgroundColor: '#6200EE' }}
          >
            Download PDF
          </Button>
        </Space>
      }
      bodyStyle={{ padding: '16px' }}
    >
      {/* Legend */}
      <MedicationLegend 
        medicationCount={medication_count}
        totalReferences={total_annotations}
      />

      {/* Category Summary */}
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        marginTop: '12px',
        flexWrap: 'wrap'
      }}>
        {categoryCounts.prescription > 0 && (
          <Tag color="gold" style={{ fontSize: '13px', padding: '4px 12px' }}>
            {categoryCounts.prescription} Prescription medications
          </Tag>
        )}
        {categoryCounts.otc > 0 && (
          <Tag color="green" style={{ fontSize: '13px', padding: '4px 12px' }}>
            {categoryCounts.otc} OTC medications
          </Tag>
        )}
        {categoryCounts.allergy > 0 && (
          <Tag color="red" style={{ fontSize: '13px', padding: '4px 12px' }}>
            {categoryCounts.allergy} Allergy-related
          </Tag>
        )}
      </div>

      <Divider style={{ margin: '16px 0' }} />

      {/* Content based on view mode */}
      {viewMode === 'list' ? (
        <MedicationList annotations={annotations} />
      ) : (
        <div style={{ position: 'relative' }}>
          {/* PDF Viewer using iframe */}
          {loading && (
            <div style={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)',
              zIndex: 10
            }}>
              <Spin size="large" tip="Loading PDF..." />
            </div>
          )}
          
          {iframeError ? (
            <div style={{ 
              padding: '60px 20px',
              textAlign: 'center',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px'
            }}>
              <ExclamationCircleOutlined style={{ fontSize: '48px', color: '#999', marginBottom: '16px' }} />
              <div style={{ marginBottom: '16px' }}>
                <Text type="secondary">
                  Unable to display PDF in browser. The annotated document has been created successfully.
                </Text>
              </div>
              <Space>
                <Button 
                  type="primary" 
                  icon={<DownloadOutlined />}
                  onClick={handleDownload}
                  style={{ backgroundColor: '#6200EE' }}
                >
                  Download Annotated PDF
                </Button>
                <Button 
                  icon={<EyeOutlined />}
                  onClick={handleOpenInNewTab}
                >
                  Open in New Tab
                </Button>
              </Space>
            </div>
          ) : (
            <div style={{ 
              border: '1px solid #e8e8e8',
              borderRadius: '8px',
              overflow: 'hidden',
              backgroundColor: '#525659'
            }}>
              {/* Toolbar */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 16px',
                backgroundColor: '#f5f5f5',
                borderBottom: '1px solid #e8e8e8'
              }}>
                <Text type="secondary">{original_filename}</Text>
                <Space>
                  <Tooltip title="Open in new tab">
                    <Button 
                      size="small" 
                      icon={<EyeOutlined />}
                      onClick={handleOpenInNewTab}
                    >
                      New Tab
                    </Button>
                  </Tooltip>
                </Space>
              </div>
              
              {/* PDF iframe */}
              <iframe
                src={pdfUrl}
                style={{
                  width: '100%',
                  height: '600px',
                  border: 'none',
                  display: loading ? 'none' : 'block'
                }}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                title="Annotated PDF Document"
              />
            </div>
          )}
        </div>
      )}

      {/* File info */}
      <div style={{ 
        marginTop: '12px', 
        textAlign: 'center',
        color: '#999',
        fontSize: '12px'
      }}>
        {original_filename}
      </div>
    </Card>
  );
};

export default AnnotatedPDFViewer;